import { useMutation, useQueryClient } from '@tanstack/react-query';
import GraphqlService from '../service/graphqlService';

/**
 * Custom React Query hooks for Data Dictionary operations
 * Handles export and import of Recipes, Events, Subevents, and Products
 */

// Export mutations (via GraphQL)
export function useExportRecipes() {
  return useMutation((projectId) => GraphqlService.exportRecipes({ projectId }));
}

export function useExportEvents() {
  return useMutation((projectId) => GraphqlService.exportEvents({ projectId }));
}

export function useExportSubevents() {
  return useMutation((projectId) => GraphqlService.exportSubevents({ projectId }));
}

export function useExportProducts() {
  return useMutation((projectId) => GraphqlService.exportProducts({ projectId }));
}

// Import mutations (via GraphQL)
export function useImportRecipes() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ projectId, file }) => GraphqlService.importRecipes({ projectId, file }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['recipes']);
      },
    }
  );
}

export function useImportEvents() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ projectId, file }) => GraphqlService.importEvents({ projectId, file }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['events']);
      },
    }
  );
}

export function useImportSubevents() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ projectId, file }) => GraphqlService.importSubevents({ projectId, file }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['subevents']);
      },
    }
  );
}

export function useImportProducts() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ projectId, file }) => GraphqlService.importProducts({ projectId, file }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['products']);
      },
    }
  );
}

/**
 * Generate a release candidate of the data dictionary
 * Creates a release candidate including recipes, products, events, subevents
 * Executes in the staging environment
 */
export function useGenerateReleaseCandidate() {
  const queryClient = useQueryClient();

  return useMutation(
    (projectId) => GraphqlService.generateReleaseCandidate({ projectId }),
    {
      onSuccess: () => {
        // Invalidate related queries on successful release candidate generation
        queryClient.invalidateQueries(['recipes']);
        queryClient.invalidateQueries(['events']);
        queryClient.invalidateQueries(['subevents']);
        queryClient.invalidateQueries(['products']);
        queryClient.invalidateQueries(['releases']);
      },
    }
  );
}

/**
 * Get existing releases from the data dictionary
 * Lists all releases for the current project
 * Executes in the production environment
 */
export function useGetReleases() {
  return useMutation((projectId) => GraphqlService.getReleases({ projectId }));
}

/**
 * Deploy the release candidate for the data dictionary
 * Releases the most recent data dictionary release candidate
 * Executes in the production environment
 */
export function useDeployReleaseCandidate() {
  const queryClient = useQueryClient();

  return useMutation(
    (projectId) => GraphqlService.deployReleaseCandidate({ projectId }),
    {
      onSuccess: () => {
        // Invalidate releases query to refresh the list after deployment
        queryClient.invalidateQueries(['releases']);
      },
    }
  );
}